interface BrandValue {
  brandId: number | string;
  name: string;
  displayName: string;
  logo: string;
}

interface Brand {
  id: number | string;
  name: string;
  logo: string;
  displayName: string;
  position: number | null;
  color: string | null;
  marketIds: number[];
  reputationMarketIds?: number[];
  type?: string;
  brandValues?: BrandValue[];
  isActive?: boolean;
  // Brand set specific fields
  ulid?: string | null;
  weighted?: boolean | null;
  members?: { id: number; weight: number; accessibleMarkets: number[]; logo?: string | null; name?: string }[];
  inaccessibleMemberBrandIds?: number[];
}
export const prepareBrandData = (group: any, brandsLookup: any): Brand[] => {
  const brandsData: any = group?.brands || [];
  const brandLookups = (brandsLookup || {}) as {
    [key: string]: {
      id: number | string;
      name: string;
      displayName: string;
      logo: string;
      color: string;
      type?: string;
    };
  };

  return brandsData.map((brand: any) => {
    const isBrandSet = brand?.type === "BRAND_SET";
    // For brand sets, use ulid as lookup key; for regular brands, use brandId
    const lookupKey = isBrandSet ? brand.ulid : String(brand.id);
    const lookup = brandLookups[lookupKey];
    const reputationMarketIds =
      brand.markets
        ?.filter((market: any) => market?.reputationDataAvailable === true)
        ?.map((market: any) => market.marketId) || [];

    const baseData: Brand = {
      id: isBrandSet ? brand.ulid : brand.id,
      name: brand?.displayName || brand?.name,
      logo: lookup?.logo || "",
      displayName: brand.displayName || brand.name,
      position: brand.order,
      color: brand.color || lookup?.color,
      marketIds: brand.markets?.map((market: any) => market.marketId),
      reputationMarketIds,
      type: brand?.type || "BRAND",
      brandValues: Array.isArray(brand?.brandValues) ? brand.brandValues : [],
    };
    
    // Add brand set specific fields
    if (isBrandSet) {
      baseData.ulid = brand.ulid;
      baseData.weighted = brand.members?.every((ele:any)=> ele.weight !== null) ?? false;
      // baseData.weighted = brand.weighted ?? false;
      baseData.members = (brand.members || []).map((member: any) => ({
        id: member.id,
        weight: member.weight,
        accessibleMarkets: Array.isArray(member.accessibleMarkets) ? member.accessibleMarkets : [],
        logo: member.logo || brandLookups[String(member.id)]?.logo || null,
        name:
          brandLookups[String(member.id)]?.displayName ||
          brandLookups[String(member.id)]?.name ||
          "",
      }));
    }
    return baseData;
  });
};

export const prepareBrandsMappingData = (brandsOrderMapping: any) => {
  const result: Record<string, (number | string)[]> = {};
  brandsOrderMapping?.forEach((value: any) => {
    if (value?.marketId) {
      result[value.marketId] = (value?.orderedBrandIds ?? []).map(
        (id: number | string) => {
          if (typeof id === 'string') {
            const parsed = Number(id);
            return !isNaN(parsed) ? parsed : id;
          }
          return id;
        }
      );
    }
  });
  return result;
};

export const prepareMarketData = (markets: any, marketLookup: any): any => {
  const brandMarketLookup = markets?.reduce((acc: any, curr: any) => {
    return {
      ...acc,
      [curr.marketId]: curr,
    };
  }, {});
  const result: any = [];
  Object.values(marketLookup)?.forEach((market: any) => {
    result.push({
      id: market?.id,
      name: market?.displayName || market?.name,
      region: market?.region,
      identity: market?.code,
      activeContractsCount: 0,
      position: brandMarketLookup?.[market?.id]?.order,
      color: market?.color,
      groupName:
        market?.region === "International" ? market?.region : market?.name,
    });
  });

  const marketGroup = result?.reduce((acc: any, curr: any) => {
    if (acc[curr?.groupName]) {
      return {
        ...acc,
        [curr.groupName]: {
          ...acc[curr.groupName],
          marketIds: [...acc[curr?.groupName]?.marketIds, curr?.id],
        },
      };
    }
    return {
      ...acc,
      [curr.groupName]: {
        id: curr?.groupName,
        name: curr?.groupName,
        marketIds: [curr?.id],
      },
    };
  }, {});

  const groupList = Object.values(marketGroup).sort((a: any, b: any) =>
    a.name.localeCompare(b.name),
  );

  return {
    markets: result,
    groups: groupList,
  };
};

export function prepareFeatureStatusData(currData: any) {
  const result: any = {};

  currData?.modules?.map((module: any) => {
    if (module?.key) {
      if (module?.enabled) {
        result[module.key === "brand-visibility" ? "visibility" : module.key] =
        {
          bysegment: true,
          bybrand: true,
          bymarket: true,
        };
      } else {
        result[module.key === "brand-visibility" ? "visibility" : module.key] =
        {
          bysegment: false,
          bybrand: false,
          bymarket: false,
        };
      }
    }
  });

  const reportBuilder = currData?.features?.find(
    (feature: any) => feature?.key === "report.builder",
  );
  const exclusiveQuestions = currData?.features?.find(
    (feature: any) => feature?.key === "exclusive.questions",
  );
  const legacyScore = currData?.features?.find(
    (feature: any) => feature?.key === "rq.scoreType.legacyScore",
  );
  const meanScore = currData?.features?.find(
    (feature: any) => feature?.key === "rq.scoreType.meanScore",
  );
  const aiAssistant = currData?.features?.find(
    (feature: any) => feature?.key === "ai.assistant",
  );

  const brandSet = currData?.features?.find(
    (feature: any) => feature?.key === "brandset",
  );

  if (reportBuilder && reportBuilder?.enabled) {
    result["reports"] = {
      enabled: true,
    };
  } else {
    result["reports"] = {
      enabled: false,
    };
  }

  if (exclusiveQuestions && exclusiveQuestions?.enabled) {
    result["exclusivequestions"] = {
      bybrand: true,
      bysegment: true,
    };
  }

  if (legacyScore && legacyScore?.enabled) {
    result["legacyScore"] = {
      enabled: true,
    };
  } else {
    result["legacyScore"] = {
      enabled: false,
    };
  }

  if (meanScore && meanScore?.enabled) {
    result["meanScore"] = {
      enabled: true,
    };
  } else {
    result["meanScore"] = {
      enabled: false,
    };
  }

  if (aiAssistant && aiAssistant?.enabled) {
    result["aiAssistant"] = {
      enabled: true,
    };
  } else {
    result["aiAssistant"] = {
      enabled: false,
    };
  }

  if (brandSet && brandSet?.enabled) {
    result["brandSet"] = {
      enabled: true,
    };
  } else {
    result["brandSet"] = {
      enabled: true,
    };
  }

  return result;
}
